"use client";

import { useRouter } from "next/navigation";
import { useRef, useState } from "react";
import ViewIcon from "@/app/components/ViewIcon";
import { showToast } from "@/app/components/Toast";
import { applyTemplate } from "@/lib/api/templates";
import type { Template } from "@/lib/api/templates";

interface Props {
  template: Template;
  /** Закрывается модалка после успешного применения (для редиректа на новый вид). */
  onApplied?: () => void;
  /** Для админа: кнопки «Редактировать» и «Удалить». */
  adminActions?: {
    onEdit: (t: Template) => void;
    onEditView?: (t: Template) => void;
    onDelete: (t: Template) => void;
  };
}

export default function TemplateCard({ template, onApplied, adminActions }: Props) {
  const router = useRouter();
  const [applying, setApplying] = useState(false);
  // Синхронная блокировка от двойного клика. useState тут не помогает —
  // между двумя быстрыми кликами React не успевает применить setState, и
  // второй клик видит `applying === false`. Реф обновляется синхронно.
  const inFlightRef = useRef(false);

  const handleApply = async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setApplying(true);
    // Safety net: на случай если router.push зависнет или юзер отменит
    // навигацию back'ом, разблокируем кнопку через 10 сек.
    const safetyTimer = setTimeout(() => {
      inFlightRef.current = false;
      setApplying(false);
    }, 10000);
    try {
      const view = await applyTemplate(template.id);
      // Флаг для HintArrow — показать одноразовую стрелку при первом
      // открытии созданного вида.
      try { localStorage.setItem("frogstat_template_just_applied", "1"); } catch {}
      // Пингуем сайдбар в ТЕКУЩЕЙ вкладке через custom event.
      window.dispatchEvent(new Event("frogstat:view-updated"));
      // Плюс cross-tab signal через localStorage — storage event автоматически
      // фаерит во всех ДРУГИХ вкладках того же origin. Используется чтобы
      // preview-вкладка могла разбудить сайдбар в opener-вкладке.
      // Значение — timestamp, чтобы каждое новое apply триггерило новое событие.
      try { localStorage.setItem("frogstat_views_ping", String(Date.now())); } catch {}
      onApplied?.();
      // Успех: гасим safety timer и router.push. Компонент размонтируется и
      // всё state-cleanup произойдёт естественно. Таймер мог бы дойти до
      // конца без clearTimeout и сделать no-op на unmounted, но лучше чисто.
      clearTimeout(safetyTimer);
      router.push(`/view/${view.id}`);
    } catch (e) {
      clearTimeout(safetyTimer);
      inFlightRef.current = false;
      setApplying(false);
      showToast(`Не удалось применить шаблон: ${(e as Error).message}`, "error");
    }
  };

  const handlePreview = () => {
    const win = window.open(`/templates/preview/${template.id}`, "_blank");
    if (!win) router.push(`/templates/preview/${template.id}`);
  };

  // Keyboard support — Enter/Space на карточке применяет шаблон (или в
  // админском режиме открывает редактирование, раз это основное действие).
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    // Не перехватываем клавиши если фокус на вложенной кнопке — тогда
    // браузер сам обработает клик на кнопке.
    if ((e.target as HTMLElement).tagName === "BUTTON") return;
    e.preventDefault();
    if (adminActions) adminActions.onEdit(template);
    else handleApply();
  };

  return (
    <div
      role={adminActions ? "group" : "button"}
      tabIndex={adminActions ? undefined : 0}
      onKeyDown={handleKeyDown}
      aria-label={adminActions ? undefined : `Применить шаблон ${template.name}`}
      className="ui-appear group flex min-h-[250px] flex-col overflow-hidden rounded-2xl border border-[#ece8e0] bg-white shadow-[0_1px_0_rgba(15,23,42,0.04)] transition-all hover:-translate-y-0.5 hover:border-[#ded8cc] hover:shadow-[0_16px_35px_rgba(15,23,42,0.10)] focus:outline-none focus:ring-2 focus:ring-emerald-300"
    >
      <div className="border-b border-[#f0ece4] bg-[#fbfaf7] p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#ece8e0] bg-white text-gray-700">
            <ViewIcon icon={template.icon ?? "lucide:Table"} size={22} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-gray-900" title={template.name}>
              {template.name}
            </div>
            <div className="mt-1 flex items-center gap-1.5">
              {template.category && (
                <span className="rounded-full bg-[#eee9df] px-2 py-0.5 text-[10px] font-medium text-gray-600">
                  {template.category}
                </span>
              )}
              {template.ownerUserId && (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                  Мой
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="h-[58px] text-xs leading-relaxed text-gray-600 line-clamp-3">
          {template.description || "Готовый набор колонок, группировок и настроек страницы."}
        </div>

        <div className="rounded-xl border border-[#ece8e0] bg-[#f8fafc] p-3">
          <div className="mb-2 flex items-center justify-between text-[10px] font-medium text-gray-500">
            <span>{template.config.columns?.length ?? 0} колонок</span>
            <span>{template.config.colGroups?.length ?? 0} групп</span>
            <span>{template.config.widgets?.length ?? 0} виджетов</span>
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="h-7 rounded-md border border-white bg-white shadow-sm"
                style={{ opacity: i < Math.min(template.config.columns?.length ?? 0, 8) ? 1 : 0.45 }}
              />
            ))}
          </div>
        </div>

        {/* В пользовательском режиме — «Предпросмотр» и «Применить». В админском
            (adminActions передан) — «Предпросмотр» + «Редактировать» + «Удалить».
            «Применить» админу не нужно: у него и так есть исходный вид. */}
        {!adminActions ? (
          <div className="mt-auto flex items-center gap-2">
            <button
              onClick={handlePreview}
              className="flex-1 rounded-xl border border-[#ece8e0] bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            >
              Предпросмотр
            </button>
            <button
              onClick={handleApply}
              disabled={applying}
              className="flex-1 rounded-xl bg-gray-900 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-gray-800 disabled:cursor-wait disabled:opacity-50"
            >
              {applying ? "Создание..." : "Применить"}
            </button>
          </div>
        ) : (
          <div className="mt-auto flex flex-col gap-2">
            {adminActions.onEditView && (
              <button
                onClick={() => adminActions.onEditView?.(template)}
                className="w-full rounded-xl bg-gray-900 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-gray-800"
              >
                Редактировать вид
              </button>
            )}
            <button
              onClick={handlePreview}
              className="w-full rounded-xl border border-[#ece8e0] bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            >
              Предпросмотр
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => adminActions.onEdit(template)}
                className="flex-1 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900"
              >
                Редактировать
              </button>
              <button
                onClick={() => adminActions.onDelete(template)}
                className="flex-1 rounded-lg px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
              >
                Удалить
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
